import videojs from 'video.js';

import { ContentStreamType, MediaErrorType } from "../../../api/presentation";

function clamp(val, min, max) {
    return Math.min(Math.max(val, min), max);
}

const SEEK_THRESHOLD = 1;
const PLAYBACK_RATE_THRESHOLD = .05;
const MAX_PLAYBACK_RATE_ADJUSTMENT = .2;
const CACHE_FRIENDLY_IMAGE_SIZES = [64, 128, 256, 512, 768];
const TEMP_FULL_SIZE_IMAGE_RES = 1024;
const MAX_ERROR_RETRIES = 100;
const RETRY_DELAY = 2000;

export default class StreamPlayer extends videojs.getComponent('Component') {
    constructor(player, options) {
        super(player, options);

        const { stream } = options;
        this.stream = stream;
        this.videojsPlayer = null;
        this.slideMapping = {};
        this.lastSync = 0;
        this.isLive = player.usingPlugin('live');
    }

    initStream() {
        const stream = this.stream;
        if (stream.streamType === ContentStreamType.Slide) {
            this.setupSlides();
        }
        else {
            this.setupVideo();
        }

        const element = this.el();
        element.style.left = `${stream.x * 100}%`;
        element.style.top = `${stream.y * 100}%`;
        element.style.width = `${stream.width * 100}%`;
        element.style.height = `${stream.height * 100}%`;
    }

    setupVideo() {
        const stream = this.stream;
        const element = document.createElement('video');
        element.setAttribute('playsinline', 'playsinline');

        this.player().multistream().log(`Initializing video stream with configuration: ${JSON.stringify(stream)}`);
        
        this.el().appendChild(element);
        const newPlayer = videojs(element, {
            controls: false,
            fill: false,
            fluid: true,
            userActions: {
                hotkeys: false
            },
            crossOrigin: 'anonymous',
            preload: 'auto',
            muted: true
        });

        //If the manifest call fails for a stream, it is likely because we are calling it before the server is ready
        //In this case attempt to recall the manifest and reload the stream-player.  Limit to 5 tries to avoid overcalling.
        //For some reason this error manifests with two different error codes, so try for both.
        //Needs to exist here and in live plugin, because error behavior is different for multistream and single stream presentations.
        let errorRetries = 0;
        newPlayer.on('error', () => {
            const error = newPlayer.error();
            if ((error.code === MediaErrorType.NetworkError || error.code === MediaErrorType.SrcNotSupported) && errorRetries < MAX_ERROR_RETRIES) {
                this.setTimeout(() => {
                    newPlayer.reset();
                    newPlayer.src(stream.items);
                    newPlayer.load();
                    newPlayer.play();
                    errorRetries++;
                }, RETRY_DELAY);
            }
        });
        newPlayer.tech(true).addClass('mediasite-player__multistream-display');

        this.on(newPlayer, ['seeked', 'loadeddata', 'waiting', 'playing', 'timeupdate'], this.trigger);

        newPlayer.src(stream.items);
        newPlayer.play() // Needed for iOS Safari to start loading
            .then(() => {
                if (this.player().paused()) {
                    newPlayer.pause();
                }
            }).catch(() => { });
        this.videojsPlayer = newPlayer;
    }

    setupSlides() {
        const stream = this.stream;
        const element = document.createElement('img');
        element.className = 'mediasite-player__multistream-display';

        //If stream is non DVR live, only load last 50 slides to reduce loading times
        const slides = (this.isLive === true && this.player().live()?.enableDvr === false) ? stream.items.slice(-50) : stream.items;
        this.player().multistream().log(`Initializing slide stream with configuration: ${JSON.stringify(slides)}`);

        this.el().appendChild(element);

        // Add a timed event for each slide
        const slideMapping = {};
        for (let i = 0; i < slides.length; i++) {
            const slide = slides[i];
            const nextSlide = slides[i + 1];
            const time = slide.time / 1000; // API returns slide times in milliseconds
            const nextTime = nextSlide ? nextSlide.time / 1000 : Infinity;

            const id = this.player().timedEvents().add(
                {
                    time,
                    duration: nextTime - time,
                    type: 'hidden'
                },
                this.handleSlideStart.bind(this),
                this.handleSlideEnd.bind(this)
            );

            const slideUrl = this.buildGenericSlideUrl(id);

            slideMapping[id] = {
                number: slide.number,
                start: time,
                end: nextTime,
                // url needed for iframe API getSlides
                url: slideUrl
            };
        }
        this.slideMapping = slideMapping;

        // temp: for testing only. TODO: trigger event on image load
        this.setTimeout(() => {
            this.trigger('loadeddata');
        }, 1000);
    }

    addSlide(slide) {
        const lastSlideId = Object.keys(this.slideMapping).find(slideId => {
            return this.slideMapping[slideId].end === Infinity;
        })
        const lastSlide = this.slideMapping[lastSlideId]

        if (lastSlide?.number >= slide.sequence) {
            //The first getLiveEvents call after page load gets all slide events from presentation start to current time
            //To save time and space, we must filter out slides that have already been added
            return
        }
        else if (lastSlide != null) {
            const newDuration = (slide.time / 1000) - lastSlide.start; // API returns slide times in milliseconds
            //Update last stored slide timed event duration from Infinity to time until start of new slide
            this.player().timedEvents().update(lastSlideId, { duration: newDuration })
            //Update last stored slide end from Infinity to start of new slide
            this.slideMapping[lastSlideId].end = slide.time / 1000;
        }

        const time = slide.time / 1000; // API returns slide times in milliseconds
        const nextTime = Infinity;
        const id = this.player().timedEvents().add(
            {
                time,
                duration: nextTime - time,
                type: 'hidden'
            },
            this.handleSlideStart.bind(this),
            this.handleSlideEnd.bind(this)
        );
        this.slideMapping[id] = {
            number: slide.sequence,
            start: time,
            end: nextTime
        };
    }

    handleSlideStart(id) {
        // TODO: use filename template from API; request appropriate width/height for current layout
        const stream = this.stream;
        const { number: slideNumber } = this.slideMapping[id];
        const playbackTicket = this.player().reporting().playbackTicket;
        const playbackTicketQuery = playbackTicket ? '?playbackticket=' + playbackTicket : '';
        const fullImageSrc = `${stream.slideBaseUrl}slide_${slideNumber.toString().padStart(4, '0')}.jpg${playbackTicketQuery} ${TEMP_FULL_SIZE_IMAGE_RES}w`;
        const srcset = CACHE_FRIENDLY_IMAGE_SIZES.map(size => {
            // Estimate the screen size at which the image is the given width in the layout
            return `${stream.slideBaseUrl}slide_${slideNumber.toString().padStart(4, '0')}_${size}_0.jpg${playbackTicketQuery} ${size}w`;
        }).join(', ').concat(', ', fullImageSrc);

        // Note: We could be even more specific if we take the aspect ratio of the composite into account. In landscape composites, for example:
        //       When we're horizontally constrained, the image changes sizes when the screen changes widths.
        //       When we're vertically constrained, the image will always be the same size (figure out the image's width in vh units).

        const imageEl = this.el().querySelector('img');
        imageEl.srcset = srcset;
        imageEl.hidden = false;
    }

    handleSlideEnd(id) {
        const currentTime = this.player().currentTime();
        const anotherSlideIsActive = Object.values(this.slideMapping).some(({ start, end }) => {
            return (start <= currentTime) && (currentTime <= end);
        });

        if (!anotherSlideIsActive) {
            const imageEl = this.el().querySelector('img');
            imageEl.srcset = '';
            imageEl.hidden = true;
        }
    }

    buildGenericSlideUrl(slideNumber) {
        const stream = this.stream;
        const playbackTicket = this.player().reporting().playbackTicket;
        const playbackTicketQuery = playbackTicket ? '?playbackticket=' + playbackTicket : '';
        const fullImageSrc = `${stream.slideBaseUrl}slide_${slideNumber.toString().padStart(4, '0')}.jpg${playbackTicketQuery}`;

        return fullImageSrc;
    }

    play() {
        return this.videojsPlayer?.play().catch(() => {});
    }

    pause() {
        this.videojsPlayer?.pause();
    }

    syncPlayback(playerTime, playbackRate) {
        return new Promise((resolve, reject) => {
            const currentTime = Date.now();
            if (this.videojsPlayer && (currentTime - this.lastSync > SEEK_THRESHOLD * 1000)) {
                this.lastSync = currentTime;
                if (this.videojsPlayer.paused()) {
                    this.videojsPlayer.play().catch(() => {});
                }

                const streamPlayerTime = this.currentTime;
                const offset = streamPlayerTime - playerTime;
                const diff = Math.abs(offset);
                const diffMs = Math.round(diff * 1000);

                if (diff > SEEK_THRESHOLD) {
                    // Stream is too far out of sync. Seek to catch up.
                    this.videojsPlayer.one('seeked', resolve);
                    this.player().multistream().log(`Secondary stream out of sync by ${diffMs}ms. Seeking to correct the difference...`);
                    this.currentTime = playerTime;
                }
                else {
                    // Within 1 second of sync. Adjust stream playback rate.
                    this.videojsPlayer.one('timeupdate', resolve);
                    if (diff > PLAYBACK_RATE_THRESHOLD) {
                        const streamPlaybackRate = this.videojsPlayer.playbackRate();
                        const adjustedPlaybackRate = clamp(playbackRate - offset, playbackRate - MAX_PLAYBACK_RATE_ADJUSTMENT, playbackRate + MAX_PLAYBACK_RATE_ADJUSTMENT);

                        if (streamPlaybackRate !== adjustedPlaybackRate) {
                            this.player().multistream().log(`Secondary stream out of sync by ${diffMs}ms. Adjusting playback rate to ${adjustedPlaybackRate}...`);
                            this.playbackRate = clamp(adjustedPlaybackRate, .25, 2);
                        }
                    }
                    else {
                        // Close enough to being in sync. Reset playbackRate to 1.0.
                        if (this.playbackRate !== 1) {
                            this.player().multistream().log(`Secondary stream close enough to being in sync. Adjusting playback rate to ${this.player().playbackRate()} to match main stream...`);
                            this.playbackRate = 1;
                        }
                    }
                }
            }
            else {
                resolve();
            }
        });
    }

    get priority() {
        return this.getAttribute('data-stream-priority')
    }
    set priority(priority) {
        const element = this.videojsPlayer?.tech(true) || this.el().querySelector('img');
        this.setAttribute('data-stream-priority', priority);
        element.setAttribute('data-stream-priority', priority);
    }

    get currentTime() {
        return this.videojsPlayer?.currentTime();
    }
    set currentTime(currentTime) {
        if (this.videojsPlayer) {
            this.videojsPlayer.currentTime(currentTime);
        }
        else {
            this.trigger('seeked');
        }
    }

    get playbackRate() {
        return this.videojsPlayer?.playbackRate();
    }
    set playbackRate(playbackRate) {
        this.videojsPlayer?.playbackRate(playbackRate);
    }
}
